import React from 'react';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {vscDarkPlus} from 'react-syntax-highlighter/dist/esm/styles/prism';

const AddMemory = ({ onSectionChange }) => {
  return (
    <div className="doc-content">
      <div className="doc-header">
        <h1>Add Memory</h1>
        <p className="doc-description">
          Learn how to create and store memories in MemU. Master the fundamentals of memory creation with practical examples and best practices.
        </p>
      </div>

      <div className="doc-section">
        <h2 id="overview">Overview</h2>
        <p>
          The <code className="function-highlight">memorize_conversation</code> function is a powerful tool for building intelligent AI systems. It allows you to store memories, enabling the creation of intelligent agents.
        </p>
      </div>

      <div className="doc-section">
        <h2 id="memory-workflow">Memory Processing Workflow</h2>
        <p>
          MemU employs a 6-stage autonomous workflow that intelligently processes and stores conversational memories without manual intervention.
          When you call <code className="function-highlight">memorize_conversation()</code>, MemU automatically analyzes the conversation content,
          extracts meaningful information, determines the appropriate storage operations, and builds intelligent connections between memories.
        </p>

        <div className="workflow-description">
          <h4>What Happens After <code className="function-highlight">memorize_conversation()</code> Call:</h4>
          <p>
            Once you invoke the memorize_conversation function with your conversation data, MemU's Autonomous Memory Agent takes over
            and performs sophisticated processing behind the scenes. The system intelligently decides what information is worth preserving,
            how to structure it for optimal retrieval, and where it fits within the existing memory network. This entire process happens
            automatically, requiring no additional input from your application.
          </p>
        </div>

        <div className="workflow-compact">
          <div className="workflow-grid">
            <div className="workflow-step-compact">
              <div className="step-number-compact">1</div>
              <div className="step-icon-compact">
                <i className="fa-solid fa-file-text"></i>
              </div>
              <h5>Content Analysis & Extraction</h5>
              <ul className="workflow-points">
                <li>Process conversation data (names, dialogue, timestamps)</li>
                <li>Advanced LLM analysis identifies key information</li>
                <li>Extract information worth preserving as memories</li>
              </ul>
            </div>

            <div className="workflow-arrow-right">
              <i className="fa-solid fa-arrow-right"></i>
            </div>

            <div className="workflow-step-compact">
              <div className="step-number-compact">2</div>
              <div className="step-icon-compact">
                <i className="fa-solid fa-lightbulb"></i>
              </div>
              <h5>Memory Suggestion Generation</h5>
              <ul className="workflow-points">
                <li>Generate structured memory recommendations</li>
                <li>Determine optimal storage categories</li>
                <li>Classify into profile, events, activities, preferences</li>
              </ul>
            </div>

            <div className="workflow-arrow-right">
              <i className="fa-solid fa-arrow-right"></i>
            </div>

            <div className="workflow-step-compact">
              <div className="step-number-compact">3</div>
              <div className="step-icon-compact">
                <i className="fa-solid fa-route"></i>
              </div>
              <h5>Operation Type Determination</h5>
              <ul className="workflow-points">
                <li>Compare against existing memories</li>
                <li>ADD for new entries, UPDATE for enhancements</li>
                <li>DELETE outdated content, TOUCH for refreshing</li>
              </ul>
            </div>

            <div className="workflow-break"></div>

            <div className="workflow-step-compact">
              <div className="step-number-compact">6</div>
              <div className="step-icon-compact">
                <i className="fa-solid fa-project-diagram"></i>
              </div>
              <h5>Memory Network Construction</h5>
              <ul className="workflow-points">
                <li>Use vector embeddings for semantic similarity</li>
                <li>Establish cross-category memory links</li>
                <li>Create entity-based associations</li>
              </ul>
            </div>

            <div className="workflow-arrow-left">
              <i className="fa-solid fa-arrow-left"></i>
            </div>

            <div className="workflow-step-compact">
              <div className="step-number-compact">5</div>
              <div className="step-icon-compact">
                <i className="fa-solid fa-cogs"></i>
              </div>
              <h5>Memory Operation Execution</h5>
              <ul className="workflow-points">
                <li>ADD operations generate unique memory IDs</li>
                <li>UPDATE operations locate and replace content</li>
                <li>DELETE removes entries, TOUCH updates metadata</li>
              </ul>
            </div>

            <div className="workflow-arrow-left">
              <i className="fa-solid fa-arrow-left"></i>
            </div>

            <div className="workflow-step-compact">
              <div className="step-number-compact">4</div>
              <div className="step-icon-compact">
                <i className="fa-solid fa-edit"></i>
              </div>
              <h5>Memory Content Standardization</h5>
              <ul className="workflow-points">
                <li>Eliminate ambiguous pronouns (he/she/it)</li>
                <li>Include complete context with full names and dates</li>
                <li>Ensure each memory stands alone</li>
              </ul>
            </div>
          </div>

          <div className="workflow-summary">
            <div className="summary-section">
              <h6>Operation Types</h6>
              <div className="operation-tags-compact">
                <span className="op-tag-compact add">ADD</span>
                <span className="op-tag-compact update">UPDATE</span>
                <span className="op-tag-compact delete">DELETE</span>
                <span className="op-tag-compact touch">TOUCH</span>
              </div>
            </div>
            <div className="summary-section">
              <h6>Key Features</h6>
              <div className="feature-list">
                <span>Autonomous</span>
                <span>Vector-based</span>
                <span>Self-contained</span>
                <span>Cross-category</span>
              </div>
            </div>
          </div>
        </div>

        <div className="info-box">
          <h4>🤖 Autonomous Memory Agent</h4>
          <p>This entire workflow operates through MemU's Autonomous Memory Agent, ensuring continuous learning and intelligent evolution.</p>
        </div>
      </div>

      <div className="doc-section">
        <h2 id="basic-creation">Example: MemU Platform</h2>
        <div className="code-block">
          <SyntaxHighlighter
            language="python"
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              padding: 0,
              background: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
{`# Create a MemU client
memu_client = MemuClient(
    base_url="https://api.memu.so",
    api_key=os.getenv("MEMU_API_KEY")
)

def do_memorize_conversation(user_id: str, user_name: str, context: list[dict]):
    # context example: [{"role": "user", "content": "hi"}, {"role": "assistant", "content": "hello"}]
    # Save to MemU
    memo_response = memu_client.memorize_conversation(
        conversation=context,
        user_id=user_id,
        user_name=user_name,
        agent_id=AGENT_ID,
        agent_name=AGENT_NAME,
    )
    print(f"💾 Saved! Task ID: {memo_response.task_id}")`}
          </SyntaxHighlighter>
        </div>
      </div>

{/*      <div className="doc-section">
        <h2 id="when-to-memorize">When to Memorize</h2>
        <p>
          Understanding when to create memories is crucial for building effective AI systems.
          Not every piece of information needs to be stored - focus on meaningful, actionable data that enhances user experience.
        </p>

        <div className="memorize-scenarios">
          <div className="scenario-card">
            <div className="scenario-icon">
              <i className="fa-solid fa-user"></i>
            </div>
            <div className="scenario-content">
              <h4>User Preferences & Settings</h4>
              <p>Store user choices, configuration preferences, and personalization settings that affect future interactions.</p>
              <div className="scenario-examples">
                <span className="example-tag">Language preferences</span>
                <span className="example-tag">Notification settings</span>
                <span className="example-tag">UI themes</span>
              </div>
            </div>
          </div>

          <div className="scenario-card">
            <div className="scenario-icon">
              <i className="fa-solid fa-calendar-days"></i>
            </div>
            <div className="scenario-content">
              <h4>Significant Events & Milestones</h4>
              <p>Capture important moments, achievements, and life events that provide context for future conversations.</p>
              <div className="scenario-examples">
                <span className="example-tag">Birthdays & anniversaries</span>
                <span className="example-tag">Career milestones</span>
                <span className="example-tag">Personal achievements</span>
              </div>
            </div>
          </div>

          <div className="scenario-card">
            <div className="scenario-icon">
              <i className="fa-solid fa-lightbulb"></i>
            </div>
            <div className="scenario-content">
              <h4>Learning & Insights</h4>
              <p>Record user learning patterns, interests, and insights that help personalize educational content and recommendations.</p>
              <div className="scenario-examples">
                <span className="example-tag">Skill levels</span>
                <span className="example-tag">Learning goals</span>
                <span className="example-tag">Topic interests</span>
              </div>
            </div>
          </div>

          <div className="scenario-card">
            <div className="scenario-icon">
              <i className="fa-solid fa-exclamation-triangle"></i>
            </div>
            <div className="scenario-content">
              <h4>Problems & Solutions</h4>
              <p>Document issues encountered and their resolutions to provide better support and avoid recurring problems.</p>
              <div className="scenario-examples">
                <span className="example-tag">Error patterns</span>
                <span className="example-tag">Successful solutions</span>
                <span className="example-tag">Troubleshooting steps</span>
              </div>
            </div>
          </div>

          <div className="scenario-card">
            <div className="scenario-icon">
              <i className="fa-solid fa-heart"></i>
            </div>
            <div className="scenario-content">
              <h4>Emotional Context & Relationships</h4>
              <p>Capture emotional states, relationship dynamics, and social context that inform empathetic responses.</p>
              <div className="scenario-examples">
                <span className="example-tag">Mood patterns</span>
                <span className="example-tag">Relationship status</span>
                <span className="example-tag">Social preferences</span>
              </div>
            </div>
          </div>

          <div className="scenario-card">
            <div className="scenario-icon">
              <i className="fa-solid fa-clock"></i>
            </div>
            <div className="scenario-content">
              <h4>Behavioral Patterns & Habits</h4>
              <p>Track recurring behaviors, usage patterns, and habits that help predict user needs and optimize experiences.</p>
              <div className="scenario-examples">
                <span className="example-tag">Usage times</span>
                <span className="example-tag">Feature preferences</span>
                <span className="example-tag">Workflow patterns</span>
              </div>
            </div>
          </div>
        </div>

        <div className="warning-box">
          <h4>⚠️ What NOT to Memorize</h4>
          <p>Avoid storing sensitive personal data (passwords, financial info), temporary states, spam content, or information that violates privacy policies.</p>
        </div>
      </div>*/}
    </div>
  );
};

export default AddMemory;

